'use client';

import React, { HTMLAttributes, useEffect, type JSX } from 'react';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import {
  IHomeShortlistModal,
  IModalsDictionary,
} from '@/dictionaries/components/modals/types';
import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { cn } from '@/lib/utils';
import { Description, Title } from '@radix-ui/react-dialog';
import dynamic from 'next/dynamic';
import { FiStar } from 'react-icons/fi';
import { Locale } from '../../../i18n.config';
import ModalContent from '../Modal/Content';
import ModalRoot from '../Modal/Root';

// Enquiry and send-to-friend forms only load when the modal opens
const ShortListBody = dynamic(
  () => import('./ShortListBody').then((mod) => mod.ShortListBody),
  { ssr: false },
);

interface IShortListButtonProps extends HTMLAttributes<HTMLDivElement> {
  dict: IHomeShortlistModal;
  villasShortListModalContent: JSX.Element;
  experiencesShortListModalContent: JSX.Element;
  shortListLength: number;
  enquiryFormDict: IEnquiryFormDictionary;
  sendToFriendFormDict: ISendToFriendsFormDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  modalsDict: IModalsDictionary;
  lang: Locale;
  hasVillas: boolean;
  hasExperience: boolean;
  simpleButton: boolean;
  modalIsOpen: boolean;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ShortListButton: React.FC<IShortListButtonProps> = ({
  simpleButton,
  dict,
  villasShortListModalContent,
  shortListLength,
  lang,
  hasVillas,
  hasExperience,
  enquiryFormDict,
  sendToFriendFormDict,
  countriesDict,
  datePickerDict,
  modalsDict,
  experiencesShortListModalContent,
  modalIsOpen,
  setModalIsOpen,
  isLoading,
  setIsLoading,
}) => {
  useEffect(() => {
    if (modalIsOpen) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    } else {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('no-scroll');
      root.classList.remove('no-scroll');
    }
  }, [modalIsOpen]);

  return (
    <>
      <ModalRoot
        open={modalIsOpen}
        onOpenChange={() => setModalIsOpen((prevState) => !prevState)}>
        <ModalContent
          preventOutsideClick
          buttonCustomClass="!bg-transparent !shadow-none text-white"
          contentCustomClass="!h-full !bg-transparent !shadow-none !w-full !max-w-none !my-0 !overflow-hidden !p-0"
          hideXButton
          onOpenChange={() => {
            setModalIsOpen((prevState) => !prevState);
          }}>
          <Title className="hidden" />
          <Description className="hidden" />
          <ShortListBody
            lang={lang}
            enquiryFormDict={enquiryFormDict}
            sendToFriendFormDict={sendToFriendFormDict}
            countriesDict={countriesDict}
            hasVillas={hasVillas}
            hasExperience={hasExperience}
            datePickerDict={datePickerDict}
            dict={dict}
            modalsDict={modalsDict}
            villasShortListModalContent={villasShortListModalContent}
            experiencesShortListModalContent={experiencesShortListModalContent}
            closeModal={() => setModalIsOpen(false)}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        </ModalContent>
      </ModalRoot>

      <button
        id={simpleButton ? 'favorited-villages' : 'favorited-villages-desktop'}
        aria-label="Open favorited villages"
        className={cn(
          simpleButton
            ? 'h-10 w-10 bg-primary flex items-center justify-center relative'
            : 'bg-primary desktop:bg-transparent p-1.5 desktop:p-0 relative',
          modalIsOpen && 'hover:translate-x-0',
          'text-white desktop:text-inherit',
        )}
        onClick={() => setModalIsOpen(true)}>
        <span
          className={`
          bg-notify/90 text-xsm text-white text-center
          rounded-sm align-middle items-center left-0 top-0 absolute -translate-x-1/2 -translate-y-1/2
          h-4 w-fit min-w-4 px-1
          ${shortListLength <= 0 && 'invisible desktop:invisible'}
          ${simpleButton && `!visible ${shortListLength <= 0 && '!invisible'}`}
        `}>
          {shortListLength}
        </span>

        <FiStar
          width={16}
          height={16}
          className={cn(
            shortListLength > 0
              ? 'desktop:fill-primary desktop:stroke-primary'
              : '',
          )}
        />
      </button>
    </>
  );
};
